import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { acceptContract, startContract } from '../../../services/contractService';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  offered:        'bg-info/10 text-info border-info/30',
  accepted:       'bg-success/10 text-success border-success/30',
  in_progress:    'bg-primary/10 text-primary border-primary/30',
  pending_review: 'bg-warning/10 text-warning border-warning/30',
  completed:      'bg-success/10 text-success border-success/30',
  cancelled:      'bg-destructive/10 text-destructive border-destructive/30',
  disputed:       'bg-warning/10 text-warning border-warning/30',
};

const STATUS_LABELS = {
  offered:        'New Offer',
  accepted:       'Accepted',
  in_progress:    'In Progress',
  pending_review: 'Pending Review',
  completed:      'Completed',
  cancelled:      'Cancelled',
  disputed:       'Disputed',
};

const ContractCard = ({ contract, onViewDetails, onMessages, onRefresh }) => {
  const [actionLoading, setActionLoading] = useState(null);

  const handleAccept = async () => {
    if (actionLoading) return;
    setActionLoading('accept');
    try {
      const result = await acceptContract(contract?.id);
      if (!result?.success) throw new Error(result?.error);
      toast.success('Contract accepted');
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to accept contract');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStart = async () => {
    if (actionLoading) return;
    setActionLoading('start');
    try {
      const result = await startContract(contract?.id);
      if (!result?.success) throw new Error(result?.error);
      toast.success('Contract started');
      onRefresh?.();
    } catch (error) {
      toast.error(error?.message || 'Failed to start contract');
    } finally {
      setActionLoading(null);
    }
  };

  const c = contract;

  const metaItems = [
    c.contract_type === 'hourly'    && { icon: 'Clock',      label: `$${c.hourly_rate?.toFixed(2)}/hr` },
    c.contract_type === 'milestone' && { icon: 'DollarSign', label: `$${c.total_contract_value?.toFixed(2)}` },
    c.budget_cap                    && { icon: 'Target',     label: `Cap $${c.budget_cap?.toFixed(2)}` },
    c.deadline                      && { icon: 'Calendar',   label: new Date(c.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
    c.case?.case_number             && { icon: 'Briefcase',  label: `Case #${c.case.case_number}` },
  ].filter(Boolean);

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6 hover:shadow-elevation-md hover:border-primary/30 transition-smooth">
      {/* Header */}
      <div className="flex items-start gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap mb-1">
            <h3 className="text-base font-semibold text-foreground">{c?.title}</h3>
            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded border flex-shrink-0 ${STATUS_COLORS[c?.status] || 'bg-muted text-muted-foreground'}`}>
              {STATUS_LABELS[c?.status] || c?.status}
            </span>
            <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded capitalize flex-shrink-0">
              {c?.contract_type}
            </span>
          </div>
          {c?.contract_number && (
            <p className="text-xs text-muted-foreground">#{c.contract_number}</p>
          )}
        </div>
      </div>

      {/* Description */}
      {c?.description && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{c.description}</p>
      )}

      {/* Meta badges */}
      {metaItems.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {metaItems.map((item) => (
            <div key={item.label} className="flex items-center gap-1.5 text-xs text-muted-foreground bg-background border border-border rounded-lg px-2.5 py-1.5">
              <Icon name={item.icon} size={12} />
              {item.label}
            </div>
          ))}
        </div>
      )}

      {/* Client info */}
      {c?.client && (
        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
          <Icon name="User" size={13} />
          <span>{c.client?.full_name || 'Client'}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-border">
        {c?.status === 'offered' && (
          <Button size="sm" onClick={handleAccept} disabled={!!actionLoading} loading={actionLoading === 'accept'} iconName="CheckCircle" iconPosition="left">
            Accept
          </Button>
        )}
        {c?.status === 'accepted' && (
          <Button size="sm" onClick={handleStart} disabled={!!actionLoading} loading={actionLoading === 'start'} iconName="Play" iconPosition="left">
            Start Work
          </Button>
        )}
        {c?.status === 'in_progress' && (
          <Button size="sm" variant="outline" onClick={onMessages} iconName="MessageSquare" iconPosition="left">
            Messages
          </Button>
        )}
        <Button
          size="sm"
          variant={['completed', 'cancelled'].includes(c?.status) ? 'default' : 'outline'}
          onClick={onViewDetails}
          iconName="Eye"
          iconPosition="left"
        >
          View Contract
        </Button>
      </div>
    </div>
  );
};

export default ContractCard;